import { useMemo, useState } from 'react'
import { Box, Button, Fab, InputLabel, MenuItem, Modal, Select, TextField, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined'
import { documentByTypeAndCountry, DocumentValidation, rolesTypesRelatedPersons } from '../constants'
import AlertFetchSpinner from '../../../components/common/alertFetchSpinner/alertFetchSpinner'
import { useFindSubjectsByKybQuery } from '../../../services/guenoApi'
import { useParams } from 'react-router-dom'
import { getCountrySegment } from '../kyb.hook-helpers'

const AddRelatedPersonModal = ({ isOpen, onChangeIsOpen, onSubmit, statusAddSubject, currentCountry, entityType }) => {
  const { id } = useParams()
  const countrySegment = getCountrySegment(currentCountry)
  const { t } = useTranslation(['common', 'messages'])
  const [relatedPersonEntity, setRelatedPersonEntity] = useState('PERSON')
  const [roleTypeRelatedPerson, setRoleTypeRelatedPerson] = useState('')
  const [relatedPersonDocumentNumber, setRelatedPersonDocumentNumber] = useState('')
  const [relationshipRelatedPerson, setRelationshipRelatedPerson] = useState('')
  const [isEmptyRelatedPerson, setIsEmptyRelatedPerson] = useState(true)
  const [attorneyEndDate, setAttorneyEndDate] = useState('')
  const [representedPerson, setRepresentedPerson] = useState('')
  const [isInvalidEndDate, setIsInvalidEndDate] = useState(false)
  const [isInvalidRepresentedPerson, setIsInvalidRepresentedPerson] = useState(false)
  const { fetchError, fetchMessage, isProcessing, isFetchingModal } = statusAddSubject

  const { data: shareholdersResponse, isLoading: isLoadingShareholders } = useFindSubjectsByKybQuery(
    {
      id,
      selectedTab: 'shareHolders',
      entityType,
      page: 0,
      limit: 100,
      sortField: 'createdAt',
      sortDirection: -1,
      countrySegment,
    },
    { skip: !isOpen },
  )

  const shareholders = useMemo(() => {
    if (!Array.isArray(shareholdersResponse?.data?.items)) return []

    return shareholdersResponse.data.items
      .filter((item) => item.type === 'PERSON')
      .map((item) => ({
        id: item._id,
        documentNumber: item.documentNumber,
        fullName: item.fullName || item.name || `${item.firstName ?? ''} ${item.lastName ?? ''}`,
      }))
  }, [shareholdersResponse])

  const viewType = 'kyc'
  const documentTypes = documentByTypeAndCountry?.[viewType]?.[currentCountry] || []

  const documentLabel =
    documentTypes.length > 1
      ? `${t('common:documentNumber')} (${documentTypes.join(' / ')})`
      : `${t('common:documentNumber')} (${documentTypes[0]})` || t('common:documentNumber')

  const documentPlaceholder = t('messages:insertDocHere', { docType: documentLabel })

  const validateDocument = (value) => {
    const currentRegex = DocumentValidation?.[viewType]?.[currentCountry]
    if (!currentRegex) return true
    return currentRegex.test(value)
  }

  return (
    <Modal
      open={isOpen}
      onClose={() => onChangeIsOpen(false)}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box className="modal">
        <Box className="modal-top">
          <Box className="modal-titles">
            <Typography variant="title">{t('common:addNewRelatedPerson')}</Typography>
            <Typography variant="subtitle3">{t('messages:editLater')}</Typography>
          </Box>
          <Fab variant="close" onClick={() => onChangeIsOpen(false)}>
            <CloseOutlinedIcon />
          </Fab>
        </Box>
        <Box className="modal-box">
          <InputLabel>{documentLabel}:</InputLabel>
          <TextField
            placeholder={documentPlaceholder}
            required
            type="text"
            value={relatedPersonDocumentNumber}
            onChange={(e) => {
              let value = e.target.value

              if (currentCountry === 'CR') {
                value = value.replace(/[^a-zA-Z0-9-]/g, '')
              } else {
                value = value.replace(/[^0-9]/g, '')
              }

              setRelatedPersonDocumentNumber(value)
            }}
            onBlur={() => {
              const isEmpty = relatedPersonDocumentNumber.trim() === ''
              const isValid = validateDocument(relatedPersonDocumentNumber)
              setIsEmptyRelatedPerson(isEmpty || !isValid)
            }}
            inputProps={{
              inputMode: currentCountry === 'CR' ? 'text' : 'numeric',
              pattern: currentCountry === 'CR' ? '[a-zA-Z0-9-]*' : '[0-9]*',
            }}
            error={isEmptyRelatedPerson}
            helperText={
              isEmptyRelatedPerson
                ? relatedPersonDocumentNumber.trim() === ''
                  ? t('messages:fieldCannotBeEmpty')
                  : t('messages:kyb.invalidCuit')
                : ''
            }
          />
        </Box>
        <Box className="modal-box">
          <InputLabel>{t('common:entityType')}:</InputLabel>
          <Select
            size="small"
            value={'PERSON'}
            disabled
            onChange={(e) => setRelatedPersonEntity(e.target.value)}
            inputProps={{ 'aria-label': 'Without label' }}
          >
            {['PERSON', 'BUSINESS']?.map((item) => (
              <MenuItem key={item} value={item}>
                {item}
              </MenuItem>
            ))}
          </Select>
          <Typography variant="subtitle4">{t('messages:shareholderDesc')}</Typography>
        </Box>
        <Box className="modal-box">
          <InputLabel>{t('common:roleType')}:</InputLabel>
          <Select
            value={roleTypeRelatedPerson}
            onChange={(e) => setRoleTypeRelatedPerson(e.target.value)}
            placeholder={`${t('common:insert')} ${t('common:roleType')}`}
          >
            {rolesTypesRelatedPersons.map((roleType) => (
              <MenuItem key={roleType} value={roleType}>
                {t(`messages:kyb.roleType.${roleType}`)}
              </MenuItem>
            ))}
          </Select>
        </Box>
        {currentCountry === 'BR' && entityType === 'BUSINESS' && roleTypeRelatedPerson === 'attorney' && (
          <>
            <Box className="modal-box">
              <InputLabel>{t('common:endDate')}:</InputLabel>
              <TextField
                type="date"
                value={attorneyEndDate}
                onChange={(e) => setAttorneyEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
                error={isInvalidEndDate}
                helperText={isInvalidEndDate ? t('messages:fieldCannotBeEmpty') : ''}
              />
            </Box>

            <Box className="modal-box">
              <InputLabel>{t('common:representedPerson')}:</InputLabel>
              <Select
                value={representedPerson}
                onChange={(e) => setRepresentedPerson(e.target.value)}
                displayEmpty
                size="small"
                disabled={isLoadingShareholders}
                error={isInvalidRepresentedPerson}
                renderValue={(selected) =>
                  selected && selected.name && selected.documentNumber
                    ? `${selected.name} (${selected.documentNumber})`
                    : t('common:selectRepresented')
                }
              >
                <MenuItem value="" disabled>
                  {t('common:selectRepresented')}
                </MenuItem>
                {shareholders.map((s) => (
                  <MenuItem
                    key={s.id}
                    value={{
                      id: s.id,
                      name: s.fullName,
                      documentNumber: s.documentNumber,
                    }}
                  >
                    {s.fullName} ({s.documentNumber})
                  </MenuItem>
                ))}
              </Select>
              {isInvalidRepresentedPerson && (
                <Typography variant="caption" color="error">
                  {t('messages:fieldCannotBeEmpty')}
                </Typography>
              )}
            </Box>
          </>
        )}

        <Box className="modal-box">
          <InputLabel>{t('common:relationship')}:</InputLabel>
          <TextField
            size="small"
            value={relationshipRelatedPerson}
            onChange={(e) => setRelationshipRelatedPerson(e.target.value)}
            placeholder={`${t('common:insert')} ${t('common:relationship')}`}
          />
        </Box>
        <Box className="modal-button">
          <Button
            size="small"
            color="secondary"
            variant="contained"
            disabled={
              isEmptyRelatedPerson || (roleTypeRelatedPerson === 'attorney' && (!attorneyEndDate || !representedPerson))
            }
            onClick={(e) => {
              let hasError = false

              if (roleTypeRelatedPerson === 'attorney') {
                if (!attorneyEndDate) {
                  setIsInvalidEndDate(true)
                  hasError = true
                } else {
                  setIsInvalidEndDate(false)
                }

                if (!representedPerson) {
                  setIsInvalidRepresentedPerson(true)
                  hasError = true
                } else {
                  setIsInvalidRepresentedPerson(false)
                }
              }

              if (hasError) return

              onSubmit(
                e,
                relatedPersonDocumentNumber,
                relatedPersonEntity,
                roleTypeRelatedPerson,
                relationshipRelatedPerson,
                attorneyEndDate,
                representedPerson,
              )
            }}
          >
            {t('common:save')}
          </Button>
          <Button size="small" color="secondary" variant="outlined" onClick={() => onChangeIsOpen(false)}>
            {t('common:cancel')}
          </Button>
          {isFetchingModal && (
            <Box className="modal-button">
              <AlertFetchSpinner message={fetchMessage} spinner={isProcessing} error={fetchError} />
            </Box>
          )}
        </Box>
      </Box>
    </Modal>
  )
}

export default AddRelatedPersonModal
